import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {UntypedFormControl, Validators} from "@angular/forms";
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {MatDialog} from '@angular/material/dialog';
import {routesNames} from "@app/app-routing.module";
import {DeliveryPoint} from "@app/core/delivery-point/application/dtos/delivery-point";
import {DeliveryPointsViewModel} from "@app/presentation/components/delivery-points/delivery-points.view-model";
import {PopupDispatcher} from "@app/presentation/components/errors/popup.dispatcher";
import {DeliveryPointService} from "@app/presentation/services/delivery-point/delivery-point.service";
import {DisplayService} from '@app/presentation/services/display.service';
import {BehaviorSubject, Observable, of} from "rxjs";
import {switchMap, take, tap} from 'rxjs/operators';
import {
    ChangeDeliveryPointBottomSheetComponent
} from './change-delivery-point-bottom-sheet/change-delivery-point-bottom-sheet.component';
import {ChangeDeliveryPointModalComponent} from './change-delivery-point-modal/change-delivery-point-modal.component';
import {LocalizedRouter} from "@app/presentation/services/localized-router";

@Component({
    selector: 'ep-delivery-points',
    templateUrl: './delivery-points.component.html'
})
export class DeliveryPointsComponent implements OnInit, OnChanges {

    @Input() deliveryPoints: DeliveryPoint[] = [];
    //Converts the input to reactive using the ngOnChange function.
    private inputDeliveryPointsSubject = new BehaviorSubject<DeliveryPoint[]>(this.deliveryPoints);
    private inputDeliveryPoints$ = this.inputDeliveryPointsSubject.asObservable();

    @Input() hasDeleteButton: boolean = false;

    @Input() radioButtonName?: string;

    @Input() autoSave: boolean = true;

    @Input() askToChangeDeliveryPoint = false;

    @Output() deliveryPointChange = new EventEmitter<DeliveryPoint>();

    @Output() canSkipDeliveryPointSelection = new EventEmitter<void>();

    @Input() shouldFetchDeliveryPoints = false;

    routesNames = routesNames;

    readonly isDeliveryPointsLoading$ = this.viewModel.isDeliveryPointsLoading$;

    deliveryPoints$: Observable<DeliveryPoint[]> = this.inputDeliveryPoints$.pipe(
        switchMap(inputDeliveryPoints => {
            if (inputDeliveryPoints.length > 1) {
                return of(inputDeliveryPoints);
            }
            return this.viewModel.getDeliveryPoints().pipe(
                tap(deliveryPoints => {
                    // If user has only one available delivery point,
                    // it will be already selected by the use case,
                    // so we can skip the selection step
                    if (deliveryPoints.length <= 1)
                        this.canSkipDeliveryPointSelection.emit()
                })
            );
        })
    );

    deliveryPointControl = new UntypedFormControl(null, Validators.required);

    isMobile$ = this.displayService.isMobileDisplay$;

    constructor(
        private deliveryPointService: DeliveryPointService,
        private viewModel: DeliveryPointsViewModel,
        private popupDispatcher: PopupDispatcher,
        private displayService: DisplayService,
        private bottomSheet: MatBottomSheet,
        private dialog: MatDialog,
        private localizedRouter: LocalizedRouter
    ) {
        this.popupDispatcher.setNotifier(viewModel.notifier)
    }

    ngOnInit(): void {
        this.reloadCurrentDeliveryPoint();
        this.initDeliveryPointFormControlSubscriber();
    }

    ngOnChanges(changes: SimpleChanges) {
        this.inputDeliveryPointsSubject.next(this.deliveryPoints)
    }

    deleteDeliveryPoint(deliveryPoint: DeliveryPoint) {
        this.deliveryPointService.openDeleteDeliveryPoint(deliveryPoint.code);
    }

    private reloadCurrentDeliveryPoint() {
        this.viewModel.getCurrentDeliveryPoint().subscribe({
            next: deliveryPoint => {
                if (deliveryPoint !== this.deliveryPointControl.value) {
                    this.deliveryPointControl.setValue(deliveryPoint, {emitEvent: false})
                }
            }
        })
    }

    private initDeliveryPointFormControlSubscriber(): void {
        this.deliveryPointControl.valueChanges.subscribe({
            next: deliveryPoint => {
                this.deliveryPointChange.emit(deliveryPoint);
                if (this.autoSave) {
                    if (this.askToChangeDeliveryPoint) {
                        this.openChangeDeliveryPoint(deliveryPoint);
                    } else {
                        this.saveDeliveryPoint(deliveryPoint);
                    }
                }
            }
        });
    }


    private openChangeDeliveryPoint(deliveryPoint: DeliveryPoint) {
        this.isMobile$.pipe(take(1)).subscribe({
            next: isMobile => {
                if (isMobile) {
                    // Open bottom sheet to confirm user's choice
                    const bottomSheet = this.bottomSheet.open(ChangeDeliveryPointBottomSheetComponent, {
                        panelClass: 'ep-bottom-sheet'
                    });

                    // Save delivery point if user has selected the bottom sheet save option
                    bottomSheet.afterDismissed().subscribe({
                        next: save => {
                            this.handleAfterCloseChangeDeliveryPoint(save, deliveryPoint);
                        }
                    });
                } else {
                    // Open modal to confirm user's choice
                    const dialog = this.dialog.open(ChangeDeliveryPointModalComponent, {
                        panelClass: 'delivery-point-modal-container'
                    });

                    // Save delivery point if user has selected the dialog save option
                    dialog.afterClosed().subscribe({
                        next: save => {
                           this.handleAfterCloseChangeDeliveryPoint(save, deliveryPoint);
                        }
                    });
                }
            }
        })
    }

    private handleAfterCloseChangeDeliveryPoint(save: boolean, deliveryPoint: DeliveryPoint) {
        if (save) {
            this.saveDeliveryPoint(deliveryPoint);
            this.navigateToHome();
        } else {
            this.reloadCurrentDeliveryPoint();
        }
    }

    private saveDeliveryPoint(deliveryPoint: DeliveryPoint) {
        this.viewModel.saveDeliveryPoint(deliveryPoint);
    }

    private navigateToHome() {
        this.localizedRouter.navigate(['/', routesNames.home]);
    }

    gotToCreateDeliveryPoint() {
        this.localizedRouter.navigate(['/', routesNames.createDeliveryPoint]);
    }
}
